import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import * as ownerApi from "../../api/payroll/payrollApi";
import * as portalApi from "../../api/payroll/payrollPortalApi";

const MONTHS = [
  "",
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/**
 * Top notification bar for pending payroll approvals.
 *
 * Props:
 *  - isPortal: boolean — use portal API & routes
 *  - enabled: boolean — show only for users who can approve (Owner / Principal)
 *  - pollMs: number — refresh interval (default 60s)
 */
export default function PayrollApprovalNotification({
  isPortal = false,
  enabled = true,
  pollMs = 60000,
}) {
  const api = isPortal ? portalApi : ownerApi;
  const basePath = isPortal ? "/staff-panel/payroll-runs" : "/payroll-runs";

  const [pending, setPending] = useState([]);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    try {
      const res = await api.getAllPayrollRuns();
      const list = Array.isArray(res) ? res : res?.data || [];
      const drafts = list.filter((r) => r.status === "DRAFT");
      setPending(drafts);
      // Re-show bar if new drafts appear after dismiss
      if (drafts.length > 0) setDismissed(false);
    } catch {
      /* silent — notification should not break the page */
    } finally {
      setLoading(false);
    }
  }, [api, enabled]);

  useEffect(() => {
    load();
    if (!enabled || !pollMs) return undefined;
    const t = setInterval(load, pollMs);
    return () => clearInterval(t);
  }, [load, enabled, pollMs]);

  if (!enabled || dismissed || pending.length === 0) return null;

  const first = pending[0];
  const more = pending.length - 1;

  return (
    <div className="fixed top-0 left-0 right-0 z-50 px-4 pt-3 pointer-events-none">
      <div className="max-w-3xl mx-auto pointer-events-auto shadow-lg rounded-xl border border-amber-200 bg-amber-50 overflow-hidden">
        <div className="flex flex-wrap items-center gap-3 px-4 py-3">
          <div className="flex-shrink-0 w-9 h-9 rounded-full bg-amber-200 flex items-center justify-center text-amber-800 font-bold text-sm">
            {pending.length}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-900">
              Payroll approval needed
            </p>
            <p className="text-xs text-amber-800 truncate">
              {MONTHS[first.month]} {first.year}
              {more > 0 ? ` and ${more} more run(s)` : ""} waiting for approval
              {loading ? " · refreshing…" : ""}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Link
              to={`${basePath}/${first._id}`}
              className="px-3 py-1.5 bg-amber-600 text-white text-sm rounded-lg hover:bg-amber-700"
            >
              Review
            </Link>
            {more > 0 && (
              <Link
                to={basePath}
                className="px-3 py-1.5 border border-amber-400 text-amber-900 text-sm rounded-lg hover:bg-amber-100"
              >
                View all
              </Link>
            )}
            <button
              type="button"
              onClick={() => setDismissed(true)}
              className="px-2 py-1.5 text-amber-700 text-sm hover:text-amber-900"
              aria-label="Dismiss"
            >
              ✕
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
